"use client";

import { useEffect, useRef, useState } from "react";
import { CalendarPlus, ClipboardCheck, HelpCircle, Menu, PhoneIncoming } from "lucide-react";

import { useBookingDrawer } from "@/lib/booking-context";
import { useFaqDrawer } from "@/lib/faq-context";
import { useMenuDrawer } from "@/lib/menu-context";
import { useQuiz } from "@/lib/quiz-context";
import { useCallbackPanel } from "@/lib/callback-context";

export default function Dock() {
  const { setOpen: setBookingOpen } = useBookingDrawer();
  const { setOpen: setFaqOpen } = useFaqDrawer();
  const { setOpen: setMenuOpen } = useMenuDrawer();
  const { setOpen: setQuizOpen } = useQuiz();
  const { setOpen: setCallbackOpen } = useCallbackPanel();
  const [visible, setVisible] = useState(false);
  const [overFooter, setOverFooter] = useState(false);
  const [bookMode, setBookMode] = useState(false);
  const dockRef = useRef<HTMLDivElement>(null);
  const shown = visible && !overFooter;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onScroll = () => {
      const nextVisible = window.scrollY > 40;
      setVisible(nextVisible);
      if (nextVisible) {
        if (!timer) {
          timer = setTimeout(() => {
            setBookMode(true);
            timer = null;
          }, 4000);
        }
      } else {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        setBookMode(false);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (timer) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const footer = document.querySelector("footer");
    const dock = dockRef.current;
    if (!footer || !dock) return;

    let observer: IntersectionObserver | null = null;
    const arm = () => {
      observer?.disconnect();
      const band = dock.offsetHeight;
      observer = new IntersectionObserver(([entry]) => setOverFooter(entry.isIntersecting), {
        rootMargin: `-${Math.max(0, window.innerHeight - band)}px 0px 0px 0px`,
      });
      observer.observe(footer);
    };
    arm();

    window.addEventListener("resize", arm);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", arm);
    };
  }, []);

  return (
    <div
      ref={dockRef}
      data-floating-dock
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 pb-[max(1rem,env(safe-area-inset-bottom))] md:hidden"
      aria-label="Primary"
      aria-hidden={!shown}
      inert={!shown}
    >
      <div
        className={`mx-auto max-w-md px-5 transition-all duration-300 ${
          shown ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
        }`}
      >
        <nav
          aria-label="Quick links"
          className="bg-navy-03/75 pointer-events-auto relative flex h-15 items-center justify-between rounded-full px-2 text-white shadow-[0_20px_48px_-12px_rgba(0,0,0,0.45)] ring-1 ring-white/15 backdrop-blur-2xl backdrop-saturate-150"
        >
          <div className="flex">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="flex h-full w-16 items-center justify-center"
              aria-label="Open menu"
            >
              <span className="flex size-12 flex-col items-center justify-center gap-1 rounded-full text-white/75 transition-colors duration-200 hover:bg-white/10 hover:text-white">
                <Menu className="size-5" strokeWidth={1.75} />
                <span className="text-[10px] leading-none font-semibold tracking-wide">Menu</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setCallbackOpen(true)}
              className="flex h-full w-16 items-center justify-center"
              aria-label="Request a callback"
            >
              <span className="flex size-12 flex-col items-center justify-center gap-1 rounded-full text-white/75 transition-colors duration-200 hover:bg-white/10 hover:text-white">
                <PhoneIncoming className="size-5" strokeWidth={1.75} />
                <span className="text-[10px] leading-none font-semibold tracking-wide">
                  Callback
                </span>
              </span>
            </button>
          </div>
          <div className="flex">
            <button
              type="button"
              onClick={() => setQuizOpen(true, "mobile-dock")}
              className="flex h-full w-16 items-center justify-center"
              aria-label="Am I suitable?"
            >
              <span className="flex size-12 flex-col items-center justify-center gap-1 rounded-full text-white/75 transition-colors duration-200 hover:bg-white/10 hover:text-white">
                <ClipboardCheck className="size-5" strokeWidth={1.75} />
                <span className="text-[10px] leading-none font-semibold tracking-wide">
                  Suitable?
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setFaqOpen(true, "mobile-dock")}
              className="flex h-full w-16 items-center justify-center"
              aria-label="Open FAQ"
            >
              <span className="flex size-12 flex-col items-center justify-center gap-1 rounded-full text-white/75 transition-colors duration-200 hover:bg-white/10 hover:text-white">
                <HelpCircle className="size-5" strokeWidth={1.75} />
                <span className="text-[10px] leading-none font-semibold tracking-wide">FAQ</span>
              </span>
            </button>
          </div>

          <button
            type="button"
            onClick={() => setBookingOpen(true, "mobile-dock")}
            aria-label="Book Consultation"
            className="bg-teal-07 absolute top-0 left-1/2 flex size-16 -translate-x-1/2 -translate-y-6.25 items-center justify-center rounded-full text-white shadow-[0_12px_28px_-6px_rgba(0,133,124,0.55)] ring-4 ring-white/20 transition-transform hover:scale-105 active:scale-95"
          >
            <span
              aria-hidden
              className={`pointer-events-none absolute inset-0 rounded-full transition-opacity duration-300 ${
                bookMode ? "opacity-0" : "opacity-100"
              }`}
              style={{
                animation: bookMode ? "none" : "dock-halo 1.8s ease-out infinite",
              }}
            />
            <span className="relative flex size-full items-center justify-center">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/assets/logo/optilase/mark-op.svg"
                alt="Optilase"
                aria-hidden
                width={5357}
                height={3539}
                className={`absolute h-5.5 w-auto translate-x-[4.9%] translate-y-[14.8%] transition-all duration-500 ease-out ${
                  bookMode ? "scale-90 opacity-0" : "scale-100 opacity-100"
                }`}
              />
              <span
                className={`absolute flex flex-col items-center justify-center gap-0.5 transition-all duration-500 ease-out ${
                  bookMode
                    ? "translate-y-0 scale-100 opacity-100"
                    : "translate-y-1 scale-95 opacity-0"
                }`}
              >
                <CalendarPlus className="size-5" strokeWidth={1.75} />
                <span className="text-[10px] leading-none font-bold tracking-wide">Book</span>
              </span>
            </span>
          </button>
        </nav>
      </div>
    </div>
  );
}
